/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global interpolate, gettext */
import React from 'react'; // eslint-disable-line
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Map, List } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import ReactRouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router';

import { MODELS, ACTIONS, can } from 'capabilities';
import { canEdit, REMOTE_CREATABLE_STATUS } from 'models/remoteCreatableObject';
import { apiRequest, transformArrayToBooleans, transformBooleansToArray } from 'utils';
import Loading from 'components/Loading';
import { isPending } from 'consts/requestStates';
import { UPDATE_ITEM, updateItem, PASS, LOAD_ITEM, TENANT, loadItem } from 'actions/items';
import { GET_COLLECTION, getAllCollection, COURSES, PASS_TYPES } from 'actions/collections';
import { resetRequestState } from 'actions/actions';
import { openConfirmationModalThen } from 'actions/uiActions';
import EditCreateTenantPassForm, { isBeforeToday } from './EditCreateTenantPassForm';


export const isFormDisabled = (
  tenantPass,
  loggedOnUser,
  isBefToday,
  disallowEditPassWithHistoricStartDate,
) =>
  !canEdit(tenantPass.get('tenant'), loggedOnUser)
  ||
  tenantPass.get('status') !== REMOTE_CREATABLE_STATUS.OK
  ||
  !can(loggedOnUser, ACTIONS.UPDATE, MODELS.TENANTS)
  ||
  (isBefToday(tenantPass.get('start_date')) && disallowEditPassWithHistoricStartDate());

export class EditTenantPass extends React.PureComponent {
  componentWillMount() {
    this.props.resetPassRequestState();
    this.props.loadCourses();
    this.props.loadPassTypes();
    this.props.loadTenant();
  }

  navigateToList() {
    const { tenantId } = this.props;
    this.props.history.push(`/tenants/${tenantId}/passes/`);
  }

  updateTenantPassAndNavigateToList(data) {
    this.props.openConfirmationModalThen(
      gettext('Are you sure you wish to update this pass?'),
      () => this.props.updateTenantPass(transformBooleansToArray(Map(data), 'courses').toJS())
        .then(() => this.navigateToList()),
    );
  }

  syncToIP(passId) {
    return apiRequest(
      '/api/passes/resync/' + passId,
      'GET',
      {},
    ).then((response) => response.json())
      .then(data => {
        if (data && data.message) {
          let error_msg = data.message;
          if (data.detail) {
            error_msg += "\n"
            for (let i = 0; i < data.detail.length; i++) {
              error_msg += "\n" + data.detail[i].msg;
            }
          }
          if (error_msg) {
            // I can feel the nerd rage eminating from the person reviewing this...
            alert(error_msg);
          }
        } else if (data && data.tenantManagerPassId) {
          // If successful, we'll get the pass data back in the JSON response.
          if (data.tenantManagerPassId === passId) {
            alert("Pass updated on IP successfully!");
          }
        }
      });
  }

  render() {
    const {
      updateRequestState,
      isLoading,
      courses,
      tenantPass,
      loggedOnUser,
      tenant,
      passTypes,
    } = this.props;

    const tenantPassTypes = tenant ? tenant.getIn(['office', 'pass_types'], List()) : List();
    const filteredPassTypes = passTypes.filter(passType => tenantPassTypes.includes(passType.get('idnumber')));

    const passId = tenantPass ? tenantPass.get('id') : '';
    const notOfficeAdmin = loggedOnUser.get('is_superuser') || loggedOnUser.get('role').get('id') !== 'OFFICE_ADMIN';

    if (isLoading) {
      return <Loading />
    }

    return (
      <div>
        <h2>{interpolate(gettext('Update tenant pass for %s'), [tenantPass.get('name')])}</h2>
        {
          notOfficeAdmin &&
          <button
            type="button"
            className="button secondary"
            onClick={() => this.syncToIP(passId)}
          >
            Sync pass data to IP
          </button>
        }
        <EditCreateTenantPassForm
          onSubmit={data => this.updateTenantPassAndNavigateToList(data)}
          onCancel={() => this.navigateToList()}
          requestState={updateRequestState}
          courses={courses}
          initialValues={transformArrayToBooleans(tenantPass, 'courses').toJS()}
          editing
          disabled={isFormDisabled(
            tenantPass,
            loggedOnUser,
            isBeforeToday,
            () => process.env.DJANGO_DISALLOW_EDIT_PASS_WITH_HISTORIC_START_DATE === 'True',
          )
          }
          passId={tenantPass.get('id')}
          passTypes={filteredPassTypes}
          tenantPass={tenantPass}
        />
      </div>
    );
  }
}

EditTenantPass.propTypes = {
  loggedOnUser: ImmutablePropTypes.map.isRequired,
  tenantId: PropTypes.string.isRequired,
  tenantPass: ImmutablePropTypes.map.isRequired,
  updateRequestState: ImmutablePropTypes.map.isRequired,
  updateTenantPass: PropTypes.func.isRequired,
  resetPassRequestState: PropTypes.func.isRequired,
  loadCourses: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  courses: ImmutablePropTypes.list.isRequired,
  isLoading: PropTypes.bool.isRequired,
  openConfirmationModalThen: PropTypes.func.isRequired,
  passTypes: ImmutablePropTypes.list.isRequired,
  tenant: ImmutablePropTypes.map,
  loadTenant: PropTypes.func.isRequired,
  loadPassTypes: PropTypes.func.isRequired,
};
EditTenantPass.defaultProps = {
  tenant: null,
};

export function mapStateToProps(state, props) {
  const { match: { params: { tenantId } } } = props;

  const isLoading = isPending(state.requestStates.getIn([GET_COLLECTION, COURSES], Map())) ||
    isPending(state.requestStates.getIn([GET_COLLECTION, PASS_TYPES], Map())) ||
    isPending(state.requestStates.getIn([LOAD_ITEM, TENANT], Map()));

  return {
    loggedOnUser: state.loggedOnUser,
    tenantId,
    tenant: state.items.get(TENANT),
    courses: state.collections.getIn([COURSES, 'items'], List()),
    updateRequestState: state.requestStates.getIn([UPDATE_ITEM, PASS], Map()),
    isLoading,
    passTypes: state.collections.getIn([PASS_TYPES, 'items'], List()),
  };
}
export function mapDispatchToProps(dispatch, props) {
  const { match: { params: { passId, tenantId } } } = props;
  return {
    updateTenantPass: data => dispatch(updateItem(PASS, passId, data)),
    resetPassRequestState: () => dispatch(resetRequestState(UPDATE_ITEM, PASS)),
    loadCourses: () => dispatch(getAllCollection(COURSES)),
    openConfirmationModalThen: (message, onConfirm) =>
      dispatch(openConfirmationModalThen(message, onConfirm)),
    loadPassTypes: () => dispatch(getAllCollection(PASS_TYPES)),
    loadTenant: () => dispatch(loadItem(TENANT, tenantId)),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditTenantPass));
